import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { toast } from "sonner";

type VersionInfo = {
  buildId: string
  builtAt: string
};

const VERSION_CHECK_QUERY_KEY = ["versionCheck"] as const;
const POLL_INTERVAL = 2 * 60 * 1000; // 2 minutes
const TOAST_ID = "new-version-available";

const fetchVersionJson = async (): Promise<VersionInfo> => {
  const response = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) throw new Error("Failed to fetch version.json");
  return response.json();
};

export const useVersionCheck = () => {
  const hasNotifiedRef = useRef(false);

  const handleNewVersion = useCallback(() => {
    if (hasNotifiedRef.current) return;
    hasNotifiedRef.current = true;

    toast.info("Une nouvelle version est disponible", {
      id: TOAST_ID,
      description: "Cliquez pour mettre à jour l'application.",
      duration: Infinity,
      closeButton: true,
      action: {
        label: "Mettre à jour",
        onClick: () => window.location.reload(),
      },
    });
  }, []);

  const enabled = import.meta.env.PROD;

  useQuery<VersionInfo>({
    queryKey: VERSION_CHECK_QUERY_KEY,
    queryFn: fetchVersionJson,
    enabled,
    refetchInterval: enabled ? POLL_INTERVAL : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    select: (data) => {
      if (data.buildId !== __BUILD_ID__) {
        handleNewVersion();
      }
      return data;
    },
  });
};
